import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FiSend, FiEye } from 'react-icons/fi';
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min.js';
import Api from '../../Api';
import { Modal, Select, MultiSelect, Button } from '../../components';
import '../../components/CompanyForm/style.css';
import './style.css';

export default function SendSpecificDocumentModal({ isOpen, onClose, companies, onSuccess }) {
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedServiceStationId, setSelectedServiceStationId] = useState('');
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [recipientType, setRecipientType] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [signatureRequired, setSignatureRequired] = useState(false);
  const [createMonthFolder, setCreateMonthFolder] = useState(false);
  const [documentType, setDocumentType] = useState('Arquivo Livre');
  
  const [documents, setDocuments] = useState([]);
  const [serviceStations, setServiceStations] = useState([]);
  const [availableRecipients, setAvailableRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);

  const RECIPIENT_TYPES = [
    { value: 'employee', label: 'Funcionário' },
    { value: 'customer', label: 'Cliente' },
    { value: 'supplier', label: 'Fornecedor' },
    { value: 'accountant', label: 'Contador' },
    { value: 'service_station', label: 'Posto de Serviço' }
  ];

  const ALL_RECIPIENTS_VALUE = 'ALL_RECIPIENTS';
  const ALL_COMPANIES_VALUE = 'ALL_COMPANIES';

  useEffect(() => {
    if (isOpen) {
        loadDocuments();
        setDocumentType('Arquivo Específico');
    } else {
        setSelectedCompanyId('');
        setSelectedServiceStationId('');
        setSelectedDocumentId('');
        setRecipientType('');
        setSelectedRecipients([]);
        setAvailableRecipients([]);
        setServiceStations([]);
        setSignatureRequired(false);
        setCreateMonthFolder(false);
        setDocumentType('Arquivo Livre');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCompanyId && selectedCompanyId !== ALL_COMPANIES_VALUE) {
        loadServiceStations();
    } else {
        setServiceStations([]);
        setSelectedServiceStationId('');
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId && recipientType) {
        loadRecipients();
    } else {
        setAvailableRecipients([]);
        setSelectedRecipients([]);
    }
  }, [selectedCompanyId, selectedServiceStationId, recipientType]);

  useEffect(() => {
    if (documentType === 'Folha de Ponto') {
        setCreateMonthFolder(true);
    }
  }, [documentType]);

  async function loadServiceStations() {
    try {
        const response = await Api.getServiceStations(selectedCompanyId);
        const stations = response.data || response;
        if (Array.isArray(stations)) {
            setServiceStations(stations);
        } else {
            setServiceStations([]);
        }
    } catch (error) {
        console.error(error);
        toast.error('Erro ao carregar postos de serviço.');
    }
  }

  async function loadDocuments() {
    try {
        setLoading(true);
        const response = await Api.getSpecificDocuments();
        const docs = response.data || response;
        if (Array.isArray(docs)) {
            setDocuments(docs);
        }
    } catch (error) {
        console.error(error);
        toast.error('Erro ao carregar documentos.');
    } finally {
        setLoading(false);
    }
  }

  async function loadRecipients() {
    try {
        setLoading(true);
        let data = [];

        if (selectedCompanyId === ALL_COMPANIES_VALUE) {
             let apiMethod;
             switch(recipientType) {
                case 'employee': apiMethod = Api.getEmployees; break;
                case 'customer': apiMethod = Api.getCustomers; break;
                case 'supplier': apiMethod = Api.getSuppliers; break;
                case 'accountant': apiMethod = Api.getAccountants; break;
                case 'service_station': apiMethod = Api.getServiceStations; break;
                default: apiMethod = null;
             }
             
             if (apiMethod) {
                 const promises = companies.map(c => apiMethod(c.id));
                 const results = await Promise.all(promises);
                 data = results.flatMap(res => {
                     const list = res.data || res;
                     return Array.isArray(list) ? list : [];
                 });
             }
        } else {
            switch(recipientType) {
                case 'employee':
                    if (selectedServiceStationId) {
                        data = await Api.getServiceStationEmployees(selectedCompanyId, selectedServiceStationId);
                    } else {
                        data = await Api.getEmployees(selectedCompanyId);
                    }
                    break;
                case 'customer':
                    data = await Api.getCustomers(selectedCompanyId);
                    break;
                case 'supplier':
                    data = await Api.getSuppliers(selectedCompanyId);
                    break;
                case 'accountant':
                    data = await Api.getAccountants(selectedCompanyId);
                    break;
                case 'service_station':
                    data = await Api.getServiceStations(selectedCompanyId);
                    break;
                default:
                    data = [];
            }
        }
        
        if (Array.isArray(data)) {
            setAvailableRecipients(data);
        } else {
            setAvailableRecipients([]);
        }
    } catch (error) {
        console.error(error);
        toast.error('Erro ao carregar destinatários.');
    } finally {
        setLoading(false);
    }
  }

  const filteredDocuments = useMemo(() => {
    if (!selectedCompanyId) return [];
    if (selectedCompanyId === ALL_COMPANIES_VALUE) return documents;
    return documents.filter(doc => String(doc.company_id) === String(selectedCompanyId));
  }, [documents, selectedCompanyId]);

  const recipientOptions = useMemo(() => {
    return [
      { value: ALL_RECIPIENTS_VALUE, label: 'TODOS' },
      ...availableRecipients.map(r => ({
        value: String(r.id),
        label: r.name || r.trade_name || r.corporate_name || r.razao_social || r.nome_fantasia || 'Sem Nome'
      }))
    ];
  }, [availableRecipients]);

  const selectedRecipientsDetails = useMemo(() => {
    if (selectedRecipients.includes(ALL_RECIPIENTS_VALUE)) {
        return availableRecipients;
    }
    return availableRecipients.filter(r => selectedRecipients.includes(String(r.id)));
  }, [selectedRecipients, availableRecipients]);

  function handleRecipientSelectChange(newValues) {
    const isAllSelected = newValues.includes(ALL_RECIPIENTS_VALUE);
    const wasAllSelected = selectedRecipients.includes(ALL_RECIPIENTS_VALUE);

    if (isAllSelected && !wasAllSelected) {
      setSelectedRecipients([ALL_RECIPIENTS_VALUE, ...availableRecipients.map(r => String(r.id))]);
      return;
    }

    if (!isAllSelected && wasAllSelected) {
      setSelectedRecipients([]);
      return;
    }

    if (wasAllSelected && newValues.length < selectedRecipients.length) {
      const remaining = newValues.filter(v => v !== ALL_RECIPIENTS_VALUE);
      setSelectedRecipients(remaining);
      return;
    }

    const specificIds = newValues.filter(v => v !== ALL_RECIPIENTS_VALUE);
    if (specificIds.length === availableRecipients.length && availableRecipients.length > 0) {
      setSelectedRecipients([ALL_RECIPIENTS_VALUE, ...specificIds]);
    } else {
      setSelectedRecipients(specificIds);
    }
  }

  function processContent(text, recipient, type, company, signatureUrl) {
     if (!text) return '';
     let content = text;
     const date = new Date().toLocaleDateString('pt-BR');
     
     // Helper para replace case-insensitive e com espaços opcionais
     const replaceTag = (tag, value) => {
        // Escapa caracteres especiais da regex se necessário, mas para tags simples não precisa
        const regex = new RegExp(`\\{\\{\\s*${tag}\\s*\\}\\}`, 'gi');
        
        let finalValue = value;
        if (finalValue === null || finalValue === undefined) {
            finalValue = '';
        }

        // Aplica uppercase se for texto e não for uma tag HTML (como a assinatura)
        if (typeof finalValue === 'string' && !finalValue.trim().startsWith('<img')) {
            finalValue = finalValue.toUpperCase();
        }
        
        content = content.replace(regex, finalValue);
     };

     // Helpers de formatação
     const formatDate = (val) => {
        if (!val) return '';
        if (typeof val === 'string' && val.includes('/')) return val;
        try {
           // Tratamento simples para YYYY-MM-DD
           if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
               const [y, m, d] = val.split('-');
               return `${d}/${m}/${y}`;
           }
           const dateObj = new Date(val);
           if (isNaN(dateObj.getTime())) return val;
           
           const d = String(dateObj.getDate()).padStart(2, '0');
           const m = String(dateObj.getMonth() + 1).padStart(2, '0');
           const y = dateObj.getFullYear();
           return `${d}/${m}/${y}`;
        } catch (e) { return val; }
     };

     const formatMoney = (val) => {
        if (!val && val !== 0) return '';
        try {
           // Se já vier com R$, retorna direto
           if (String(val).includes('R$')) return val;
           const num = parseFloat(String(val).replace(',', '.'));
           if (isNaN(num)) return val;
           return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        } catch (e) { return val; }
     };

     replaceTag('DATA_ATUAL', date);
     
     if (company) {
         replaceTag('NOME_EMPRESA', company.corporate_name || company.trade_name);
         replaceTag('REPRESENTANTE_EMPRESA', company.representative);
         replaceTag('CNPJ_EMPRESA', company.cnpj);
     }

     if (signatureUrl) {
         // Se houver assinatura, substitui a tag pela imagem
         const imgTag = `<img src="${signatureUrl}" alt="Assinatura da Empresa" style="max-height: 100px; max-width: 200px;" />`;
         replaceTag('ASSINATURA_EMPRESA', imgTag);
     } else {
         replaceTag('ASSINATURA_EMPRESA', '');
     }

     // Normalização dos dados do destinatário
     const rName = recipient.name || recipient.trade_name || recipient.corporate_name || recipient.razao_social || recipient.nome_fantasia || '';
     const rDoc = recipient.cpf || recipient.cnpj || recipient.cpf_cnpj || '';
     const rRg = recipient.rg || '';
     const rEmail = recipient.email || '';
     const rPhone = recipient.phone || recipient.cellphone || '';
     const rAddress = recipient.address || '';
     const rJourney = recipient.journey || '';

     // Replaces específicos por tipo
     if (type === 'employee') {
         replaceTag('NOME_FUNCIONARIO', rName);
         replaceTag('CPF_FUNCIONARIO', rDoc);
         replaceTag('RG_FUNCIONARIO', rRg);
         replaceTag('EMAIL_FUNCIONARIO', rEmail);
         replaceTag('CELULAR_FUNCIONARIO', rPhone);
         
         // Monta endereço completo se disponível
         const endCompleto = [
            recipient.address_street,
            recipient.address_number,
            recipient.address_complement,
            recipient.address_district,
            recipient.address_city,
            recipient.address_state,
            recipient.address_zip_code
         ].filter(Boolean).join(', ');
         
         replaceTag('ENDERECO_EMPREGADO', endCompleto || rAddress);

         replaceTag('NOME_PAI', recipient.father_name);
         replaceTag('NOME_MAE', recipient.mother_name);
         replaceTag('DATA_NASCIMENTO', formatDate(recipient.birth_date));
         replaceTag('NOME_CONJUGE', recipient.spouse_name); // Campo precisa vir do backend
         replaceTag('CATEGORIA', recipient.category?.name || recipient.category_name);
         
         replaceTag('TITULO_ELEITORAL', recipient.voter_title);
         replaceTag('ZONA_ELEITORAL', recipient.voter_zone);
         replaceTag('SECAO_ELEITORAL', recipient.voter_section);
         
         replaceTag('DATA_OPCAO', formatDate(recipient.option_date));
         replaceTag('RNE', recipient.rne);
         replaceTag('CASADO_BRASILEIRO', recipient.married_with_brazilian ? 'Sim' : 'Não');
         
         replaceTag('CARGO', recipient.position?.name || recipient.position_name);
         replaceTag('PROFISSAO_DRT', recipient.drt_profession);
         replaceTag('REGISTRO_DRT', recipient.drt_registration);
         
         replaceTag('TIPO_SANGUINEO', recipient.blood_type);
         replaceTag('DATA_ADMISSAO', formatDate(recipient.admission_date));
         replaceTag('PIS_PASEP', recipient.pis_pasep);
         
         replaceTag('JORNADA_FUNCIONARIO', recipient.journey?.name || recipient.journey_name || rJourney);
         
         replaceTag('NACIONALIDADE', recipient.nationality);
         replaceTag('ESTADO_CIVIL', recipient.civil_status);
         
         replaceTag('ORGAO_EXPEDIDOR', recipient.rg_issuer);
         replaceTag('NATURALIDADE', recipient.birth_place);
         replaceTag('SERIE_UF', recipient.ctps_series); // Assumindo série ou UF
         
         replaceTag('IDENTIDADE', recipient.rg);
         replaceTag('DATA_EXPEDICAO_IDENTIDADE', formatDate(recipient.rg_issue_date));
         
         replaceTag('GRAU_INSTRUCAO', recipient.education_level);
         
         replaceTag('CTPS', recipient.ctps_number);
         replaceTag('DATA_EXPEDICAO_CTPS', formatDate(recipient.ctps_issue_date));
         replaceTag('DATA_CHEGADA', formatDate(recipient.arrival_date));
         
         replaceTag('DATA_NATURALIZACAO', formatDate(recipient.naturalization_date));
         replaceTag('PORTARIA_NATURALIZACAO', recipient.naturalization_ordinance);
         replaceTag('NATURALIZADO', recipient.is_naturalized ? 'Sim' : 'Não');
         replaceTag('FILHO_BRASIL', recipient.has_brazilian_child ? 'Sim' : 'Não');
         
         replaceTag('LOTACAO', recipient.lotacao);
         replaceTag('CBO', recipient.cbo);
         
         replaceTag('DATA_RECICLAGEM', formatDate(recipient.last_recycling_date));
         replaceTag('DATA_FORMACAO', formatDate(recipient.professional_training_date));
         
         replaceTag('FOLHA_DRT', recipient.drt_page);
         replaceTag('LIVRO_DRT', recipient.drt_book);
         
         replaceTag('DEFICIENCIA', (recipient.is_pcd === true || recipient.is_pcd === 'true') ? 'Sim' : 'Não');
         
         replaceTag('SALARIO_VARIAVEL', formatMoney(recipient.variable_salary));
         replaceTag('FORMA_PAGAMENTO', recipient.payment_method);
         replaceTag('SALARIO', formatMoney(recipient.salary));
         
         // Novos campos mapeados do formData
         replaceTag('SEXO_FUNCIONARIO', recipient.sex);
         
         const mapTipoContrato = (t) => {
             if (t === 'C') return 'CLT';
             if (t === 'PJ') return 'Pessoa Jurídica';
             if (t === 'E') return 'Estágio';
             return t;
         };
         replaceTag('TIPO_CONTRATO', mapTipoContrato(recipient.type));
         
         replaceTag('STATUS_FUNCIONARIO', recipient.status);
         replaceTag('MATRICULA', recipient.registration_number);
         replaceTag('HOMEOFFICE', (recipient.homeoffice === true || recipient.homeoffice === 'true') ? 'Sim' : 'Não');
         replaceTag('HORARIO_INICIO', recipient.work_start_time);
         replaceTag('ULTIMAS_FERIAS', recipient.last_vacation_period);
         replaceTag('ALTURA', recipient.height);
         replaceTag('PESO', recipient.weight);
         replaceTag('TIPO_DEFICIENCIA', recipient.disability_type);
         
         replaceTag('BANCO_NOME', recipient.bank_name);
         replaceTag('BANCO_AGENCIA', recipient.bank_agency);
         replaceTag('BANCO_CONTA', recipient.bank_account);
         replaceTag('BANCO_TIPO_CONTA', recipient.bank_account_type);
         
         replaceTag('TEM_PIX', (recipient.has_pix === true || recipient.has_pix === 'true') ? 'Sim' : 'Não');
         replaceTag('PIX_TIPO', recipient.pix_key_type);
         replaceTag('PIX_CHAVE', recipient.pix_key);
         
         replaceTag('SALARIO_FAMILIA', (recipient.has_family_benefit === true || recipient.has_family_benefit === 'true') ? 'Sim' : 'Não');
         replaceTag('NUM_DEPENDENTES', recipient.dependents_count);

         // replaceTag('ASSINATURA_FUNCIONARIO', ''); // Placeholder - Mantido as chaves conforme solicitação
     } else if (type === 'service_station') {
         replaceTag('POSTO_SERVICO', rName);
         replaceTag('ENDERECO_POSTO', rAddress);
     } 
     
     // Replaces genéricos e variações comuns
     replaceTag('NOME', rName);
     replaceTag('NOME_CLIENTE', rName);
     replaceTag('NOME_FORNECEDOR', rName);
     replaceTag('RAZAO_SOCIAL', rName);
     
     replaceTag('CPF', rDoc);
     replaceTag('CNPJ', rDoc);
     replaceTag('DOCUMENTO', rDoc);
     
     replaceTag('ENDERECO', rAddress);
     replaceTag('EMAIL', rEmail);
     
     replaceTag('ASSINATURA_DESTINATARIO', '');

     return content;
  }

  function generateHtml(title, content, signature) {
      // Retorna apenas o conteúdo interno e estilos escopados para evitar quebrar o layout da app
      return `
        <div class="pdf-document-wrapper">
            <div class="content">${content}</div>
        </div>
      `;
  }

  async function handleSend() {
    if (!selectedCompanyId || !selectedDocumentId || !recipientType || selectedRecipients.length === 0) {
        toast.warning('Preencha todos os campos.');
        return;
    }

    setSending(true);
    try {
        const docTemplate = documents.find(d => String(d.id) === String(selectedDocumentId));
        if (!docTemplate) throw new Error('Documento não encontrado.');

        const selectedCompany = companies.find(c => String(c.id) === String(selectedCompanyId));
        
        let successCount = 0;
        const datePart = new Date().toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
        const targetCategory = documentType === 'Folha de Ponto' ? 'ponto' : 'geral';
        const targetFolderName = documentType === 'Folha de Ponto' ? `${datePart} - Arquivos de Ponto` : datePart;

        const recipientsList = selectedRecipients.filter(id => id !== ALL_RECIPIENTS_VALUE);
        for (const recipientId of recipientsList) {
            const recipient = availableRecipients.find(r => String(r.id) === String(recipientId));
            if (!recipient) continue;

            let targetCompanyId = selectedCompanyId;
            let targetCompany = selectedCompany;

            if (selectedCompanyId === ALL_COMPANIES_VALUE) {
                targetCompanyId = recipient.company_id;
                if (targetCompanyId) {
                    targetCompany = companies.find(c => String(c.id) === String(targetCompanyId));
                }
            }

            if (!targetCompanyId) {
                 console.warn(`Could not determine company for recipient ${recipientId}`);
                 continue;
            }

            let folderId = null;
            if (createMonthFolder) {
                 try {
                     const folderOptions = {};
                     let empId = null;
                     let stationId = null;
                     
                     if (recipientType === 'employee') empId = recipient.id;
                     else if (recipientType === 'service_station') stationId = recipient.id;
                     else if (recipientType === 'customer') folderOptions.customerId = recipient.id;
                     else if (recipientType === 'supplier') folderOptions.supplierId = recipient.id;
                     else if (recipientType === 'accountant') folderOptions.accountantId = recipient.id;

                     console.log(`Buscando pasta ${targetFolderName} para recipient ${recipient.id} (${recipientType}) - Categoria: ${targetCategory}`);
                     const foldersResponse = await Api.getFolders(targetCompanyId, null, stationId, empId, targetCategory, folderOptions);
                     const folders = Array.isArray(foldersResponse) ? foldersResponse : (foldersResponse.data || []);
                     
                     const existingFolder = folders.find(f => f.name === targetFolderName);

                     if (existingFolder) {
                         folderId = existingFolder.id;
                         console.log(`Pasta existente encontrada: ${folderId}`);
                     } else {
                         const folderPayload = {
                             name: targetFolderName,
                             company_id: targetCompanyId,
                             parent_id: null,
                             category: targetCategory
                         };
                         if (recipientType === 'employee') folderPayload.employee_id = recipient.id;
                         else if (recipientType === 'service_station') folderPayload.service_station_id = recipient.id;
                         else if (recipientType === 'customer') folderPayload.customer_id = recipient.id;
                         else if (recipientType === 'supplier') folderPayload.supplier_id = recipient.id;
                         else if (recipientType === 'accountant') folderPayload.accountant_id = recipient.id;

                         console.log('Criando nova pasta...', folderPayload);
                         const newFolderResponse = await Api.createFolder(folderPayload);
                         
                         // Verifica se retornou ID diretamente ou dentro de data
                         folderId = newFolderResponse.id || newFolderResponse.data?.id;
                         
                         // Se não retornou ID, tenta buscar a pasta novamente (pode ter sido criada mas a API não retornou o ID)
                         if (!folderId) {
                             console.warn('Criação de pasta não retornou ID. Tentando buscar a pasta novamente...', newFolderResponse);
                             const retryFoldersResponse = await Api.getFolders(targetCompanyId, null, stationId, empId, targetCategory, folderOptions);
                             const retryFolders = Array.isArray(retryFoldersResponse) ? retryFoldersResponse : (retryFoldersResponse.data || []);
                             const createdFolder = retryFolders.find(f => f.name === targetFolderName);
                             
                             if (createdFolder) {
                                 folderId = createdFolder.id;
                                 console.log(`Pasta recuperada após criação: ${folderId}`);
                             }
                         }
                         
                         if (!folderId) {
                            console.error('Falha ao criar/recuperar pasta: Resposta sem ID', newFolderResponse);
                            toast.error(`Falha ao criar pasta para ${recipient.name || recipientId}. O arquivo será salvo na raiz.`);
                         } else {
                            console.log(`Nova pasta confirmada: ${folderId}`);
                         }
                     }
                 } catch (err) {
                     console.error("Error managing folder for recipient " + recipient.id, err);
                     toast.error(`Erro ao gerenciar pasta para ${recipient.name || recipientId}. O arquivo será salvo na raiz.`);
                 }
            }
            
            // Verifica se o template usa a tag de assinatura
            const hasSignatureTag = /{{\s*assinatura_empresa\s*}}/i.test(docTemplate.content || '');
            
            // Passa a assinatura para o processContent para substituir a tag, se existir
            const processedContent = processContent(docTemplate.content, recipient, recipientType, targetCompany, docTemplate.signature);
            const processedTitle = processContent(docTemplate.title, recipient, recipientType, targetCompany, null);

            // Se usou a tag no conteúdo, não anexa a assinatura no rodapé (passa null)
            // Se NÃO usou a tag, mantém o comportamento padrão de anexar no rodapé (passa docTemplate.signature)
            const signatureForFooter = hasSignatureTag ? null : docTemplate.signature;

            const htmlContent = generateHtml(processedTitle, processedContent, signatureForFooter);
            
             const safeTitle = processedTitle.replace(/[^a-z0-9áàâãéèêíïóôõöúçñ ]/gi, '_').trim();
             
             // Cria um iframe para isolar completamente o CSS e a renderização
             const iframe = document.createElement('iframe');
             Object.assign(iframe.style, {
                position: 'fixed',
                left: '0',
                top: '0',
                width: '210mm',
                height: '297mm',
                border: 'none',
                zIndex: '-9999',
                visibility: 'visible', // Necessário para renderização
                opacity: '0', // Esconde visualmente mas mantém renderizável
                background: '#ffffff' // Garante fundo branco
             });
             
             document.body.appendChild(iframe);

             // Escreve o conteúdo no iframe
             const iframeDoc = iframe.contentWindow.document;
             iframeDoc.open();
             iframeDoc.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>${processedTitle}</title>
                    <style>
                        body { 
                            font-family: Arial, sans-serif; 
                            margin: 0;
                            padding: 20mm; /* Margem do papel */
                            color: #333; 
                            line-height: 1.6; 
                            background: white;
                            box-sizing: border-box;
                            width: 100%;
                            height: 100%;
                        }
                        .content { margin-top: 20px; white-space: pre-wrap; }
                        img { max-width: 100%; }
                    </style>
                </head>
                <body>
                    <div class="content">${processedContent}</div>
                </body>
                </html>
             `);
             iframeDoc.close();

             // Aguarda carregamento de recursos (imagens) no iframe
             await new Promise(resolve => {
                 iframe.onload = resolve;
                 // Fallback caso onload não dispare ou já tenha carregado
                 setTimeout(resolve, 1000); 
             });

             const opt = {
                 margin: [0, 0, 0, 0], // Margens já definidas no CSS do body do iframe
                 filename: `${safeTitle}.pdf`,
                 image: { type: 'jpeg', quality: 0.98 },
                 html2canvas: { 
                    scale: 2, 
                    useCORS: true, 
                    letterRendering: true,
                    scrollY: 0,
                    windowWidth: iframe.contentWindow.document.body.scrollWidth,
                    windowHeight: iframe.contentWindow.document.body.scrollHeight
                 },
                 jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
             };

             let pdfBlob;
             try {
                 // Renderiza o body do iframe
                 pdfBlob = await html2pdf().from(iframe.contentWindow.document.body).set(opt).output('blob');
             } catch (pdfError) {
                 console.error('Erro ao gerar PDF:', pdfError);
                 toast.error('Erro ao gerar PDF. O arquivo será salvo como HTML.');
                 pdfBlob = new Blob([htmlContent], { type: 'text/html' });
             } finally {
                 document.body.removeChild(iframe);
             }

             const fileType = pdfBlob.type === 'application/pdf' ? 'application/pdf' : 'text/html';
             const fileExtension = pdfBlob.type === 'application/pdf' ? '.pdf' : '.html';
             
             const file = new File([pdfBlob], `${safeTitle}${fileExtension}`, { type: fileType });

             const reader = new FileReader();
             const base64 = await new Promise((resolve) => {
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(file);
             });

             const payload = {
                name: file.name,
                folder_id: folderId, 
                company_id: targetCompanyId,
                file_data: base64,
                html_content: htmlContent,
                file_type: file.type,
                file_size: file.size,
                signature_required: signatureRequired ? 1 : 0,
                is_signed: 0,
                status: signatureRequired ? 'Pendente de Assinatura' : 'Enviado',
                document_type: documentType,
                category: targetCategory
             };

             if (recipientType === 'employee') payload.employee_id = recipient.id;
             if (recipientType === 'customer') payload.customer_id = recipient.id;
             if (recipientType === 'supplier') payload.supplier_id = recipient.id;
             if (recipientType === 'accountant') payload.accountant_id = recipient.id;
             if (recipientType === 'service_station') payload.service_station_id = recipient.id;

             await Api.createFile(payload);
             successCount++;
        }

        toast.success(`${successCount} documentos enviados com sucesso!`);
        if (onSuccess) onSuccess();
        onClose();

    } catch (error) {
        console.error(error);
        toast.error('Erro ao enviar documentos.');
    } finally {
        setSending(false);
    }
  }

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Enviar Documento Específico"
      size="md"
    >
        <div className="form-body">
            <div className="form-grid form-component">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <Select
                    label="Empresa"
                    options={[
                        {value: '', label: 'SELECIONE UMA EMPRESA...'},
                        {value: ALL_COMPANIES_VALUE, label: 'TODAS'},
                        ...companies.map(c => ({ value: c.id, label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}` }))
                    ]}
                    value={selectedCompanyId}
                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                    searchable
                />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <Select
                    label="Posto de Serviço"
                    options={[{value: '', label: 'Selecione um posto...'}, ...serviceStations.map(s => ({ value: s.id, label: s.name }))]}
                    value={selectedServiceStationId}
                    onChange={(e) => setSelectedServiceStationId(e.target.value)}
                    disabled={!selectedCompanyId || selectedCompanyId === ALL_COMPANIES_VALUE || serviceStations.length === 0}
                    searchable
                />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <Select
                        label="Tipo de Documento"
                        options={[
                            { value: 'Arquivo Específico', label: 'Arquivo Específico' },
                            { value: 'Arquivo Livre', label: 'Arquivo Livre' },
                            { value: '13º Salário', label: '13º Salário' },
                            { value: 'Contra Cheque', label: 'Contra Cheque' },
                            { value: 'Folha de Ponto', label: 'Folha de Ponto' }
                        ]}
                        value={documentType}
                        onChange={(e) => setDocumentType(e.target.value)}
                        disabled
                    />
                  </div>
                  <div className="form-group">
                    <Select
                        label="Documento"
                        options={[{value: '', label: 'SELECIONE...'}, ...filteredDocuments.map(d => ({ value: d.id, label: d.title }))]}
                        value={selectedDocumentId}
                        onChange={(e) => setSelectedDocumentId(e.target.value)}
                        disabled={!selectedCompanyId}
                        searchable
                    />
                  </div>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <Select
                    label="Tipo de Destinatário"
                    options={[{value: '', label: 'SELECIONE...'}, ...RECIPIENT_TYPES]}
                    value={recipientType}
                    onChange={(e) => setRecipientType(e.target.value)}
                    disabled={!selectedCompanyId}
                />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                        <MultiSelect
                          label={"Destinatários"}
                          options={recipientOptions}
                          value={selectedRecipients}
                          onChange={handleRecipientSelectChange}
                          disabled={!recipientType || availableRecipients.length === 0}
                          placeholder="Selecione os destinatários..."
                        />
                    </div>
                </div>
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns:  '1fr', gap: '1rem', marginTop: '0.5rem' }}>
                <div 
                  className={`feature-toggle-container ${signatureRequired ? 'active' : ''}`}
                  onClick={() => setSignatureRequired(!signatureRequired)}
                  style={{ marginBottom: 0 }}
                >
                  <div className="toggle-track">
                    <div className="toggle-thumb" />
                  </div>
                  <div className="toggle-content">
                    <div className="toggle-title">Assinatura Obrigatória</div>
                    <div className="toggle-description">Solicitar assinatura digital</div>
                  </div>
                </div>

                <div 
                  className={`feature-toggle-container ${createMonthFolder ? 'active' : ''}`}
                  onClick={() => setCreateMonthFolder(!createMonthFolder)}
                  style={{ marginBottom: 0 }}
                >
                  <div className="toggle-track">
                    <div className="toggle-thumb" />
                  </div>
                  <div className="toggle-content">
                    <div className="toggle-title">Organizar arquivos por Mês/Ano</div>
                    <div className="toggle-description">
                      Cria automaticamente a pasta Mês/Ano para o destinatário. Se a pasta já existir, os arquivos serão salvos nela.
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
        <div className="modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={onClose}
            disabled={sending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={sending || selectedRecipients.length === 0} startIcon={<FiSend />}>
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
    </Modal>

    <Modal
        isOpen={showRecipientsModal}
        onClose={() => setShowRecipientsModal(false)}
        title="Destinatários Selecionados"
        size="md"
    >
        <div className="form-body">
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {selectedRecipientsDetails.map(r => (
                    <div key={r.id} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                        <strong>{r.name || r.trade_name || r.corporate_name || r.razao_social || r.nome_fantasia}</strong>
                        <div style={{ fontSize: '12px', color: '#666' }}>{r.cpf || r.cnpj || r.cpf_cnpj}</div>
                    </div>
                ))}
            </div>
        </div>
        <div className="modal-actions">
            <Button onClick={() => setShowRecipientsModal(false)}>Fechar</Button>
        </div>
    </Modal>
    </>
  );
}
